import { computed, ref } from 'vue'

import {
  alertBoxClass,
  buildMaintenancePageLoadState,
  progressClass,
  type ImpersonationLoginState,
  type ProgressState,
} from '@/models/current-student-advanced-standing-form/appSession'
import { sanitizeHtml } from '@/models/current-student-advanced-standing-form/maintenanceSanitizer'
import type {
  PublicFormApiResponse,
  StudentFormLoadData,
} from '@/models/current-student-advanced-standing-form/api'

export function useStudentLoginState() {
  const pageLoadMessage = ref('Loading...')
  const pageLoadMessageIsError = ref(false)
  const studentDetailsVisible = ref(false)
  const loginErrorMessage = ref('')
  const loginProgressVisible = ref(false)
  const loginProgressMessage = ref('..')
  const loginProgressState = ref<ProgressState>('active')
  const loginPending = ref(false)
  const loginValidated = ref(false)
  const impersonateMessageVisible = ref(false)
  const username = ref('')
  const password = ref('')

  const pageLoadMessageClass = computed(() => alertBoxClass(pageLoadMessageIsError.value))
  const loginProgressClass = computed(() => progressClass(loginProgressState.value))

  function startPageLoad(): void {
    pageLoadMessage.value = 'Loading...'
    pageLoadMessageIsError.value = false
    studentDetailsVisible.value = false
  }

  function showPageLoadError(message: string): void {
    pageLoadMessage.value = message
    pageLoadMessageIsError.value = true
  }

  function applyMaintenanceState(
    data: StudentFormLoadData,
    user: PublicFormApiResponse<StudentFormLoadData>['user'],
  ): void {
    const state = buildMaintenancePageLoadState(data, user, sanitizeHtml)
    pageLoadMessage.value = state.pageLoadMessage
    pageLoadMessageIsError.value = state.pageLoadMessageIsError
    studentDetailsVisible.value = state.studentDetailsVisible
  }

  function applyLoginMaintenanceState(data: StudentFormLoadData): void {
    pageLoadMessage.value = sanitizeHtml(data.maintenance_mode_message ?? '')
    pageLoadMessageIsError.value = true
    studentDetailsVisible.value = false
  }

  function applyImpersonationState(impersonationState: ImpersonationLoginState | null): void {
    if (!impersonationState) return

    loginValidated.value = impersonationState.loginValidated
    impersonateMessageVisible.value = impersonationState.impersonateMessageVisible
    username.value = impersonationState.username
    password.value = impersonationState.password
  }

  function resetLoginInputState(): void {
    loginValidated.value = false
    loginProgressVisible.value = false
  }

  function startLoginValidation(): void {
    loginProgressMessage.value = 'Validating Account...'
    loginProgressVisible.value = true
    loginProgressState.value = 'active'
    loginPending.value = true
    loginErrorMessage.value = ''
  }

  function finishLoginRequest(): void {
    loginPending.value = false
  }

  function completeLoginValidation(): void {
    loginValidated.value = true
    loginProgressState.value = 'success'
    loginProgressMessage.value = 'Success.'
  }

  function failLoginValidation(message: string): void {
    loginPending.value = false
    loginValidated.value = false
    loginProgressState.value = 'error'
    loginProgressMessage.value = 'Error'
    loginErrorMessage.value = message
  }

  function clearPassword(): void {
    password.value = ''
  }

  return {
    pageLoadMessage,
    pageLoadMessageIsError,
    studentDetailsVisible,
    loginErrorMessage,
    loginProgressVisible,
    loginProgressMessage,
    loginProgressState,
    loginPending,
    loginValidated,
    impersonateMessageVisible,
    username,
    password,
    pageLoadMessageClass,
    loginProgressClass,
    startPageLoad,
    showPageLoadError,
    applyMaintenanceState,
    applyLoginMaintenanceState,
    applyImpersonationState,
    resetLoginInputState,
    startLoginValidation,
    finishLoginRequest,
    completeLoginValidation,
    failLoginValidation,
    clearPassword,
  }
}
